// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
// models
import type { ResourceType } from '../../types/resource';
import { fetchSharedResources } from '../../actions/shared';
// shared components
import RecordShareAlert from '../shared-resource-banner';
import AppView from '../layouts/AppView';
import DashboardCards from '../dashboard-cards';
import Dropdown from '../shared/DropdownCustom';
import Text from '../shared/Typography';
import FilterList, { FilterButton } from '../shared/FilterList';
import Section from '../shared/PageSection';
import Loader from '../shared/Loader';
import PageHeader from '../shared/PageHeader';
// utils
import sortingData from '../../data/sorting';
import { alpha } from '../../utils/utils';
import { getRouteTFunction } from '../../i18n';

type Props = {
  resources: ResourceType[],
  fetchResources: (token: string) => void,
  fetchError: string,
  token: string,
  accessToken: string,
  sharedAt: string,
  plural: boolean,
  loading: boolean,
  user: string,
  match?: Object,
  location?: {
    search?: string
  },
  history: Object
} & WithTranslation

type State = {
  selectedTypes: ResourceType[],
  selectedPatients: string[]
}

class DashboardShared extends Component<Props, State> {
  state: State = {
    loading: false,
    activeTypes: [],
    activeYears: [],
    activeProviders: [],
    sortingType: 'dateDesc'
  }

  componentDidMount() {
    const { match } = this.props;
    const token = match && match.params && match.params.token;
    const { fetchResources } = this.props;
    fetchResources(token);
  }

  componentDidUpdate() {
    const { match, fetchError } = this.props;
    const routesTFunction = getRouteTFunction();
    const token = match && match.params && match.params.token;
    const accessToken = localStorage.getItem(`access_token_${token}`);
    if (!accessToken || (fetchError && fetchError.match(/expire|revoke|access denied/i))) {
      this.props.history.push({
        pathname: routesTFunction('redirectPaths./shared/:token', { token }),
        search: this.props.location.search
      });
    }
  }

  getFilteredResources = (resources) => {
    const { activeTypes, activeYears, activeProviders } = this.state;
    // const query = this.state.searchQuery.toLowerCase();
    let filteredResources = resources;

    if (activeTypes.length) {
      filteredResources = filteredResources
        .filter((resource) => activeTypes
          .includes(resource.type));
    }

    if (activeYears.length) {
      filteredResources = filteredResources
        .filter((resource) => activeYears
          .includes(resource.sortByDate.format('YYYY')));
    }

    if (activeProviders.length) {
      filteredResources = filteredResources
        .filter((resource) => activeProviders
          .includes(resource.institution_name));
    }

    // if (query && query.length) {
    //   filteredResources = filteredResources.filter((res) => (
    //     this.containsQuery(res.searchableText)
    //   ));
    // }
    return filteredResources;
  }


  sortResources = (a, b, sortingAttribute, ascending = true) => {
    if (!ascending) {
      if (a[sortingAttribute] < b[sortingAttribute]) return 1;
      if (b[sortingAttribute] < a[sortingAttribute]) return -1;
    } else if (ascending) {
      if (a[sortingAttribute] < b[sortingAttribute]) return -1;
      if (b[sortingAttribute] > a[sortingAttribute]) return 1;
    }
    return null;
  }

  updateType = (filter) => {
    let newActiveTypes = [];
    const existsInFilterList = this.state.activeTypes.includes(filter);
    if (this.state.activeTypes.length > 0) newActiveTypes = this.state.activeTypes;
    if (existsInFilterList) newActiveTypes = newActiveTypes.filter((item) => item !== filter);
    else if (!existsInFilterList) newActiveTypes.push(filter);
    return this.setState({ activeTypes: newActiveTypes });
  }

  updateYear = (year) => {
    let newActiveYears = [];
    const existsInFilterList = this.state.activeYears.includes(year);
    if (this.state.activeYears.length > 0) newActiveYears = this.state.activeYears;
    if (existsInFilterList) newActiveYears = newActiveYears.filter((item) => item !== year);
    else if (!existsInFilterList) newActiveYears.push(year);
    return this.setState({ activeYears: newActiveYears });
  }

  updateProvider = (provider) => {
    let newActiveProviders = [];
    const existsInFilterList = this.state.activeProviders.includes(provider);
    if (this.state.activeProviders.length > 0) newActiveProviders = this.state.activeProviders;
    if (existsInFilterList) {
      newActiveProviders = newActiveProviders.filter((item) => item !== provider);
    } else if (!existsInFilterList) newActiveProviders.push(provider);
    return this.setState({ activeProviders: newActiveProviders });
  }

  generateTypes = () => {
    const { resources } = this.props;
    let allTypes = resources && resources.map((res) => res.type);
    allTypes = new Set(allTypes);
    allTypes = Array.from(allTypes);
    allTypes = alpha(allTypes);
    return allTypes;
  }

  generateYears = () => {
    const { resources } = this.props;
    let allYears = resources && resources.map((res) => res && res.sortByDate.format('YYYY'));
    allYears = new Set(allYears);
    allYears = Array.from(allYears);
    return allYears;
  }

  generateProviders = () => {
    const { resources } = this.props;
    let allProviders = resources && resources.map((res) => {
      if (res && res.institution_name && res.institution_name !== null) return res.institution_name;
      return null;
    });
    allProviders = allProviders.filter((provider) => !!provider);
    allProviders = new Set(allProviders);
    allProviders = Array.from(allProviders);
    allProviders = alpha(allProviders);
    return allProviders;
  }

  clearFilters = () => {
    this.setState({ activeTypes: [], activeYears: [], activeProviders: [] });
  }

  setSortType = (sortingType) => {
    this.setState({ sortingType });
  }

  filters = (t) => (
    <div>
      <Text paragraph bold>{t('sort_by')}</Text>
      <div className='records-filter__list'>
        {Object.keys(sortingData).map((key) => (
          <FilterButton
            item={sortingData[key].name}
            name={sortingData[key].label}
            selectedItems={[this.state.sortingType]}
            onSelectFilter={() => this.setState({ sortingType: sortingData[key].label })}
          />))}
      </div>
      <Text paragraph bold>{t('filter_by_year')}</Text>
      <FilterList
        onSelectFilter={this.updateYear}
        listItems={this.generateYears()}
        selectedTypes={this.state.activeYears}
        className='horizontal'
      />
      <Text paragraph bold>{t('filter_by_record_type')}</Text>
      <FilterList
        onSelectFilter={this.updateType}
        listItems={this.generateTypes()}
        selectedTypes={this.state.activeTypes}
        className='horizontal'
      />
      <Text paragraph bold>{t('filter_by_health_provider')}</Text>
      <FilterList
        onSelectFilter={this.updateProvider}
        listItems={this.generateProviders()}
        selectedTypes={this.state.activeProviders}
        className='horizontal'
      />
    </div>
  )

  props: Props;

  render() {
    const { resources, sharedAt, user, plural, loading, match, t } = this.props;
    const token = match && match.params && match.params.token;
    const { sortingType } = this.state;

    const getFilteredResources = resources && this.getFilteredResources(resources);
    const filteredResources: ResourceType[] = getFilteredResources
      .sort((a, b) => {
        // user specified sort order
        if (sortingType === 'dateAsc') return this.sortResources(a, b, 'sortByDate');
        if (sortingType === 'dateDesc') return this.sortResources(a, b, 'sortByDate', false);
        if (sortingType === 'alphaAsc') return this.sortResources(a, b, 'title');
        if (sortingType === 'alphaDesc') return this.sortResources(a, b, 'title', false);
        return 0;
      }).sort((a, b) => {
        // pinned resources
        return this.sortResources(a, b, 'sortingPriority', false);
      });

    const allResources: number = resources && resources.length;
    const totalFilteredResources: number = filteredResources && filteredResources.length;

    const renderFullDash = Boolean(!loading && resources && resources.length > 0);

    let countText = '';
    switch (allResources) {
    case 0:
      countText = 'No records';
      break;
    case 1:
      countText = '1 record';
      break;
    case totalFilteredResources:
      countText = `${allResources} records`;
      break;
    default:
      countText = `${totalFilteredResources} of ${allResources} records`;
    }

    return (
      <AppView
        viewType='private-body'
        frameless
        shareBanner={
          <RecordShareAlert
            shared_at={sharedAt}
            user={user}
            plural={plural}
          />
        }
      >
        {loading && <Loader isLoading />}
        <div className='dashboard'>
          {!renderFullDash &&
            <PageHeader
              className='verification'
              illustrationClass={'illo-welcome-desktop'}
              title={t('welcome')}
              subtitle={t('no_records')}
            />
          }
          {renderFullDash && (
            <Section
              title={t('shared_records')}
              subtitle={countText}
            >
              {/* show on desktop */}
              <div className='records-filter__sidebar'>
                {this.filters(t)}
              </div>
              {/* show on mobile */}
              <Dropdown label={t('filters_and_sort')} className='records-filter__dropdown'>
                <div className='records-filter__dropdown__content'>
                  {this.filters(t)}
                </div>
              </Dropdown>
              <DashboardCards
                resources={filteredResources}
                shared
                token={token}
              />
            </Section>
          )}
        </div>
      </AppView>
    );
  }
}

const mapStateToProps = (appState) => ({
  loading: appState.shared && appState.shared.loading,
  resources: appState.sharedResources && appState.sharedResources.data,
  fetchError: appState.sharedResources && appState.sharedResources.error,
  plural: appState.sharedResources && appState.sharedResources.data && appState.sharedResources.data.length > 1,
  user: appState.sharedResources
    && appState.sharedResources.data
    && appState.sharedResources.data[0]
    && appState.sharedResources.data[0].patientName,
  sharedAt: appState.shared && appState.shared.data && appState.shared.data.shared_at
});

const mapDispatchToProps = (dispatch) => ({
  fetchResources: ((token) => dispatch(fetchSharedResources(token)))
});

export default withTranslation('dashboard')(connect(mapStateToProps, mapDispatchToProps)(DashboardShared));



// WEBPACK FOOTER //
// ./src/components/pages/dashboard-shared.js